// Turning a data type's resolver into the phrase the Data Types grid shows.
//
// Extracted rather than left in the table so the tests exercise what ships. A copy in a test file
// proves the copy works, which is the failure mode where a rendering bug and a passing suite coexist
// indefinitely.
// The resolver kinds, and which key each one names its target with. One place, because the form
// that writes a resolver and the reader that takes one apart have to agree about this.
//
// AccountUser maps to nothing on purpose: its population is the caller's own tenant, so there is no
// target to name. That is a complete resolver, not an unfinished one, and the form has to know the
// difference or it will demand a value nobody can supply.
export const RESOLVER_TARGET_KEY: Record<string, string | null> = {
    ObjectClassification: "classification",
    PickList: "pickType",
    AccountUser: null,
    Lookup: "lookup",
};

// What each kind calls the thing a person reads in the picker. One map beside the other, because
// the two are not the same question and assuming they were is how a Lookup field came to ask for an
// attribute LOOKUP_VALUE does not have.
//
// A LOOKUP VALUE HAS NO "Name". Objects and pick values do, which is why "Name" was written
// unconditionally when those were the only kinds; a lookup value carries VALUE and LABEL, so a
// resolver written with the old default would ask for a column that is not there.
export const RESOLVER_DISPLAY_ATTRIBUTE: Record<string, string> = {
    ObjectClassification: "Name",
    PickList: "Name",
    AccountUser: "Name",
    Lookup: "Label",
};

export const RESOLVER_KINDS = Object.keys(RESOLVER_TARGET_KEY);

// A stored resolver, taken apart into the two things a person actually supplies.
//
// WITHOUT THIS, REVISING A REFERENCE TYPE SILENTLY BROKE IT. The detail form read its target from an
// attribute nothing ever populated, so opening Revise showed an empty box and saving wrote a resolver
// naming nothing -- refused by the service if you were lucky, and a type that admits no values if the
// refusal ever softened.
export function decodeResolver(raw: unknown): {kind: string; target: string} {
    if (!raw) {
        return {kind: "ObjectClassification", target: ""};
    }
    try {
        const text = typeof raw === "string" ? atob(raw) : JSON.stringify(raw);
        const resolver = JSON.parse(text);
        const kind = resolver.kind || "ObjectClassification";
        const key = RESOLVER_TARGET_KEY[kind];
        return {kind, target: (key && resolver[key]) || ""};
    } catch (e) {
        return {kind: "ObjectClassification", target: ""};
    }
}

export function resolverSummary(kind: string | null | undefined, raw: unknown): string {
    if (kind !== "Reference") {
        // Not blank. A Composite or Primitive has no resolver, and blank reads as MISSING — which is
        // not a state these kinds have.
        return "—";
    }
    if (!raw) {
        return "—";
    }
    try {
        // Base64 JSON on the wire, like every JSON attribute in the platform.
        const text = typeof raw === "string" ? atob(raw) : JSON.stringify(raw);
        const resolver = JSON.parse(text);
        // Each kind names its target with a different key; showing only one made every pick list
        // read as its kind, which says nothing about WHICH list.
        const key = RESOLVER_TARGET_KEY[resolver.kind];
        const target = key && resolver[key];
        return target ? `${resolver.kind}: ${target}` : resolver.kind || "—";
    } catch (e) {
        // A resolver that will not parse is a broken definition. Saying so in the cell is more use
        // than an empty one, which looks deliberate.
        return "unreadable";
    }
}
